'use client';
import { useCallback, useEffect, useRef, useState } from 'react';

import { debounce, throttle } from 'lodash-es';
import { useQuery } from '@tanstack/react-query';

import fetchCountries from '@utils/api/locations';

export const useIsMount = (): boolean => {
  const isMountRef = useRef(true);
  useEffect(() => {
    isMountRef.current = false;
  }, []);
  return isMountRef.current;
};
export const useLoadingState = (
  shouldShowAnimationOnTransitionTo: (destinationURL: string) => boolean = () =>
    true
) => {
  const [isLoading, setIsLoading] = useState(false);

  const showLoading = useCallback(
    (destinationURL?: string) => {
      if (
        !destinationURL ||
        shouldShowAnimationOnTransitionTo(destinationURL)
      ) {
        setIsLoading(true);
      }
    },
    [shouldShowAnimationOnTransitionTo]
  );

  const hideLoading = useCallback(() => {
    setIsLoading(false);
  }, []);

  return {
    isLoading,
    showLoading,
    hideLoading,
  };
};
export const useOutsideClick = (ref: any, callback: any): void => {
  useEffect(() => {
    const handleOutsideClick = (event: MouseEvent) => {
      if (ref.current && !ref.current.contains(event.target as Node)) {
        callback?.();
      }
    };
    document.addEventListener('mousedown', throttle(handleOutsideClick, 200));
    return () => {
      document.removeEventListener('mousedown', handleOutsideClick);
    };
  }, [callback, ref]);
};

export const useScroll = (): {
  x: number;
  y: number;
  direction: string;
} => {
  const [scroll, setScroll] = useState({ x: 0, y: 0, direction: '' });

  useEffect(() => {
    setScroll((prev) => ({
      x: document.body.getBoundingClientRect().left,
      y: -document.body.getBoundingClientRect().top,
      // Here we’re comparing the previous state to the current state to get the scroll direction
      direction:
        prev.y > -document.body.getBoundingClientRect().top ? 'up' : 'down',
    }));

    const listener = () => {
      setScroll((prev) => ({
        x: document.body.getBoundingClientRect().left,
        y: -document.body.getBoundingClientRect().top,
        // Here we’re comparing the previous state to the current state to get the scroll direction
        direction:
          prev.y > -document.body.getBoundingClientRect().top ? 'up' : 'down',
      }));
    };
    window.addEventListener('scroll', debounce(listener, 200), {
      passive: true,
    });
    // cleanup function occurs on unmount
    return () => window.removeEventListener('scroll', listener);
  }, []);

  return scroll;
};

export const useChangeEvent = (
  defaultValue?: string
): [string, (value: string) => void] => {
  const [value, setValue] = useState<string>(defaultValue || '');

  const handleChange = (value: string): void => {
    setValue(value);
  };

  return [value, handleChange];
};

const getCustomDeviceConfig = (width: number, limit: number) => {
  if (width <= limit - 1) return { xl: false, sm: true };
  else return { xl: true, sm: false };
};
export const useCustomMobileDesktopBreakpoint = (limit: number) => {
  const [breakPoint, setBreakpoint] = useState<{ xl?: boolean; sm?: boolean }>({
    xl: false,
    sm: false,
  });

  useEffect(() => {
    const calcWidth = throttle(function () {
      setBreakpoint(getCustomDeviceConfig(window.innerWidth, limit));
    }, 200);
    calcWidth();
    window.addEventListener('resize', calcWidth);
    return () => window.removeEventListener('resize', calcWidth);
  }, []);

  return breakPoint;
};

export const useScrollDirection = () => {
  const [scrollDir, setScrollDir] = useState('');

  useEffect(() => {
    const threshold = 0;
    let lastScrollY = window.pageYOffset;
    let ticking = false;

    const updateScrollDir = () => {
      const scrollY = window.pageYOffset;

      if (Math.abs(scrollY - lastScrollY) < threshold) {
        ticking = false;
        return;
      }
      setScrollDir(scrollY > lastScrollY ? 'down' : 'up');
      lastScrollY = scrollY > 0 ? scrollY : 0;
      ticking = false;
    };

    const onScroll = () => {
      if (!ticking) {
        window.requestAnimationFrame(updateScrollDir);
        ticking = true;
      }
    };

    window.addEventListener('scroll', debounce(onScroll, 200), {
      passive: true,
    });

    return () => window.removeEventListener('scroll', onScroll);
  }, [scrollDir]);

  return scrollDir;
};

type UseDebounceOptions<T> = {
  getKey?: (V: T) => unknown;
  getValue?: (key: unknown) => T;
  delay?: number;
};
export function useDebounceValue<T>(
  value: T,
  {
    delay = 300,
    getKey = (value) => value,
    getValue = (key) => key as T,
  }: UseDebounceOptions<T> = {}
) {
  const [debouncedValue, setDebouncedValue] = useState<T>(value);

  const key = getKey(value);
  useEffect(() => {
    const timer = setTimeout(() => setDebouncedValue(getValue(key)), delay);

    return () => {
      clearTimeout(timer);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [key, delay]);

  return debouncedValue;
}

export const useCountries = () => {
  const { data } = useQuery(['useCountries'], fetchCountries, {
    staleTime: Infinity,
    cacheTime: Infinity,
  });

  return data || [];
};
